<script setup lang="ts">
import { ref } from 'vue'
import type { UploadedResult } from '@/types/conversation.type'
import { DocumentAdd, CloseBold } from '@element-plus/icons-vue'

// Props
interface Props {
  visible: boolean
  attachment: UploadedResult | null
  // 添付の種類ごとに署名付き URL の取得先が違う
  getDownloadUrl: (key: string) => Promise<string>
}

// Emits
const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const props = defineProps<Props>()

// State
const downloadUrl = ref<string>('')
const isLoading = ref(false)

// Watch for dialog open
import { watch } from 'vue'
watch(
  () => props.visible,
  async (newVal) => {
    if (newVal && props.attachment) {
      await loadAttachment()
    } else if (!newVal) {
      downloadUrl.value = ''
    }
  },
)
// Load attachment
async function loadAttachment() {
  if (!props.attachment) return

  isLoading.value = true
  try {
    const url = await props.getDownloadUrl(props.attachment.key)
    downloadUrl.value = url
  } catch (error) {
    console.error('Failed to get download URL:', error)
  } finally {
    isLoading.value = false
  }
}

// Download file
function handleDownload() {
  if (downloadUrl.value) {
    window.open(downloadUrl.value, '_blank')
  }
}

// Close dialog
function handleClose() {
  emit('update:visible', false)
}
</script>

<template>
  <el-dialog
    :model-value="visible"
    width="95%"
    :show-close="false"
    :append-to-body="true"
    :before-close="handleClose"
    @update:model-value="(val: boolean) => emit('update:visible', val)"
  >
    <template #header="{ close, titleId, titleClass }">
      <div class="dialog-header">
        <h4 :id="titleId" :class="titleClass">{{ attachment?.originalName || 'File' }}</h4>
        <el-button :icon="CloseBold" circle @click="close" />
      </div>
    </template>
    <div class="attachment-dialog-content">
      <div v-if="isLoading" class="loading-state">
        <span>Loading file...</span>
      </div>
      <div v-else-if="!isLoading && downloadUrl">
        <!-- Image preview -->
        <div v-if="attachment?.fileType === 'image'" class="image-preview">
          <img :src="downloadUrl" :alt="attachment.originalName" />
        </div>
        <!-- PDF or other files -->
        <div v-else class="file-download">
          <el-icon :size="48"><DocumentAdd /></el-icon>
          <p>{{ attachment?.originalName }}</p>
          <el-button type="primary" @click="handleDownload"> Download </el-button>
        </div>
      </div>
      <div v-else-if="!downloadUrl && !isLoading" class="error-state">
        <span>Failed to load file</span>
      </div>
    </div>
    <template #footer>
      <el-button @click="handleClose">Close</el-button>
    </template>
  </el-dialog>
</template>

<style scoped>
.attachment-dialog-content {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  .loading-state,
  .error-state {
    text-align: center;
    color: #909399;
    font-size: var(--font-size-body);
    padding: 40px;
  }

  .image-preview {
    text-align: center;

    img {
      max-width: 100%;
      max-height: calc(60dvh - env(safe-area-inset-bottom));
      border-radius: 8px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    }
  }

  .file-download {
    text-align: center;
    padding: 40px;

    .el-icon {
      color: #909399;
      margin-bottom: 16px;
    }

    p {
      font-size: var(--font-size-body);
      color: #303133;
      margin: 0 0 24px 0;
      word-break: break-all;
    }
  }
}
</style>
